/**
 * Report configuration for the admin data experience, which contains:
 *   - Which reports exist and their metadata (path, label, nav category)
 *   - Which reports are available per product
 *
 * Mirrors `config/reports.ts` rather than sharing it. That config answers what
 * a *product* offers a teacher — drill-downs, assessments, per-product-code
 * exclusions — while this answers what the admin experience has built. They
 * will diverge as admin reports land, and a shared config would have each side
 * filtering out most of the other's entries.
 *
 * To add a new report: add an entry to ADMIN_REPORTS and list its path against
 * the products that support it.
 * To add a new product: add an entry to ADMIN_PRODUCT_REPORTS (or to
 *   ADMIN_MAGAZINE_CODES for magazine products).
 */

export type AdminReportPath = 'overview';

export type AdminReportCategory = 'Usage';

export interface AdminReportConfig {
  /** URL path segment under /data/:productId/ — also used as the unique key */
  path: AdminReportPath;
  /** Display label used in navigation */
  label: string;
  /** Nav grouping category (UI only, not in URL) */
  category: AdminReportCategory;
}

export const ADMIN_REPORTS: AdminReportConfig[] = [
  {
    path: 'overview',
    label: 'Overview',
    category: 'Usage',
  },
];

/**
 * All magazine (CM group) product codes.
 * To add a new magazine product, simply add its code to this set.
 */
const ADMIN_MAGAZINE_CODES = [
  'junior',
  'action',
  'letsfindout',
  'math',
  'mybigworld',
  'sciencespin2',
  'sciencespin36',
  'scienceworld',
  'scope',
  'sn1',
  'storyworks1',
  'art',
  'choices',
  'sciencespink1',
  'sn2',
  'sn3',
  'sn4',
  'sn56',
  'storyworks',
  'storyworksjr',
  'upfront',
  'ahora',
  'cheznous',
  'quetal',
  'storyworks2',
  'superstem',
  'allonsy',
  'bonjour',
  'cava',
  'elsol',
] as const;

export const ADMIN_MAGAZINE_PRODUCT_CODES = new Set<string>(
  ADMIN_MAGAZINE_CODES
);

const ADMIN_MAGAZINE_REPORTS: AdminReportPath[] = ['overview'];

/**
 * Per-product report configuration for non-magazine products.
 * Magazine products are handled via ADMIN_MAGAZINE_PRODUCT_CODES above.
 *
 * Every product is overview-only today — that is the one admin report built —
 * so this reads as repetitive on purpose: the shape is what lets a product
 * gain a report without the nav changing shape.
 */
const ADMIN_PRODUCT_REPORTS_INTERNAL = {
  WORD: ['overview'],
  FIRST: ['overview'],
  LITPRO: ['overview'],
  R4R: ['overview'],
  KL: ['overview'],
  ScholasticLiteracy: ['overview'],
  rise: ['overview'],
} as const satisfies Record<string, readonly AdminReportPath[]>;

export const ADMIN_PRODUCT_REPORTS: Record<string, readonly AdminReportPath[]> =
  ADMIN_PRODUCT_REPORTS_INTERNAL;

/**
 * Product-code-level report exclusions.
 *
 * For a given applicationCode, maps a report path to the set of product codes
 * that, when they are the ONLY products held for that app, hide the report.
 * Empty while Overview is the only report — Overview applies to every product —
 * and kept so an admin report that does vary by product code has a home.
 */
export const ADMIN_REPORT_PRODUCT_CODE_EXCLUSIONS: Record<
  string,
  Partial<Record<AdminReportPath, string[]>>
> = {};

// ── Product Code Types ─────────────────────────────────────────────────────────

/** Non-magazine product codes (WORD, FIRST, LITPRO, R4R, rise). */
export type AdminNonMagazineProductCode =
  keyof typeof ADMIN_PRODUCT_REPORTS_INTERNAL;

/** Magazine product codes. */
export type AdminMagazineProductCode = (typeof ADMIN_MAGAZINE_CODES)[number];

/** All product codes the admin dashboard supports. */
export type AdminProductCode =
  | AdminNonMagazineProductCode
  | AdminMagazineProductCode;

/** Non-magazine product codes — derived from ADMIN_PRODUCT_REPORTS keys. */
export const ADMIN_NON_MAGAZINE_PRODUCTS: AdminNonMagazineProductCode[] =
  Object.keys(ADMIN_PRODUCT_REPORTS_INTERNAL) as AdminNonMagazineProductCode[];

/** Returns true if the given product code is a magazine product. */
export function isAdminMagazineEdApp(
  edApp: AdminProductCode
): edApp is AdminMagazineProductCode {
  return ADMIN_MAGAZINE_PRODUCT_CODES.has(edApp);
}

/** Returns true if the product code is supported by the admin dashboard. */
export function isAdminSupportedProduct(productCode: string): boolean {
  return (
    productCode in ADMIN_PRODUCT_REPORTS ||
    ADMIN_MAGAZINE_PRODUCT_CODES.has(productCode)
  );
}

/**
 * Returns the AdminReportConfig objects available for a given product.
 * Returns an empty array if the product is unknown.
 *
 * @param productId - The application code (e.g. 'R4R', 'LITPRO').
 * @param userProductCodes - Optional array of the product codes held for this
 *   application. When provided, exclusions from
 *   ADMIN_REPORT_PRODUCT_CODE_EXCLUSIONS are applied.
 */
export function getAdminReportsForProduct(
  productId: string,
  userProductCodes?: string[]
): AdminReportConfig[] {
  const allowed =
    ADMIN_PRODUCT_REPORTS[productId] ??
    (ADMIN_MAGAZINE_PRODUCT_CODES.has(productId) ? ADMIN_MAGAZINE_REPORTS : []);

  let reports = ADMIN_REPORTS.filter((report) => allowed.includes(report.path));

  const exclusions = ADMIN_REPORT_PRODUCT_CODE_EXCLUSIONS[productId];
  if (userProductCodes && userProductCodes.length > 0 && exclusions) {
    reports = reports.filter((report) => {
      const excludedByProducts = exclusions[report.path];
      if (!excludedByProducts) return true;
      // Hide the report only if ALL of the product codes held for this app are
      // within the exclusion list.
      const hasOnlyExcludedProducts = userProductCodes.every((code) =>
        excludedByProducts.includes(code)
      );
      return !hasOnlyExcludedProducts;
    });
  }

  return reports;
}

/**
 * Returns reports grouped by category, in the order they appear in
 * ADMIN_REPORTS.
 *
 * @param productId - The application code (e.g. 'R4R', 'LITPRO').
 * @param userProductCodes - Optional array of the product codes held for this
 *   application. Passed through to getAdminReportsForProduct.
 *
 * @example
 * const grouped = getAdminReportsGroupedByCategory('LITPRO');
 * // { Usage: [{ path: 'overview', label: 'Overview', ... }] }
 */
export function getAdminReportsGroupedByCategory(
  productId: string,
  userProductCodes?: string[]
): Partial<Record<AdminReportCategory, AdminReportConfig[]>> {
  const reports = getAdminReportsForProduct(productId, userProductCodes);
  return reports.reduce<
    Partial<Record<AdminReportCategory, AdminReportConfig[]>>
  >((acc, report) => {
    const group = acc[report.category] ?? [];
    return { ...acc, [report.category]: [...group, report] };
  }, {});
}
